import { useNavigate, useParams } from "react-router-dom";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import { ReportMetadataItem } from "../../../../models/pageModels/ReportLibrary";
import { generatePlaceholderReportName } from "../../../../utils/reportHelpers";
import { formatJSONDateWithoutTime } from "../../../../utils/dateHelpers";
import { useReportViewStore } from "../../../../store/report/useReportViewStore";
import { useRefreshLock } from "../../../../hooks/reports/useRefreshLock";
import { useReportMutations } from "../../../../hooks/reports/mutations/useReportMutations";
import { useModalStore } from "../../../../store/report/modalStore";
import { DELETE_REPORT, REVERT_DRAFT, TOGGLE_BOOKMARK } from "../../../../constants/reportActions";
import DropDownWithPortal from "../../../UI/DropDownWithPortal";

interface SidePanelItemProps {
  report: ReportMetadataItem;
  isSelectable?: boolean;
}

const SidePanelItem = ({ report, isSelectable = false }: SidePanelItemProps) => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  
  const isSelected = useReportViewStore((s) => s.selectedReports.includes(report.id));
  const toggleSelection = useReportViewStore((s) => s.toggleReportSelection);
  const openModal = useModalStore((s) => s.openModal);
  
  const { actionMutation } = useReportMutations();
  // A report in the sidebar is locked exactly when its parent group is
  // refreshing. If the report does not belong to a group, it cannot be
  // locked, and useRefreshLock returns isLocked: false in that case.
  const { isLocked } = useRefreshLock({ groupId: report.reportGroupId ?? undefined });

  const displayName = report.name ?? generatePlaceholderReportName({ reportType: report.reportType, created: report.created });
  
  // Format dates
  const createdStr = formatJSONDateWithoutTime(report.created, "yyyy-MM-dd");
  const lastRunStr = formatDistanceToNow(new Date(report.lastRun), { addSuffix: true });
  
  // Tooltip now only needs the Created date, as Last Run is visible
  const infoTooltip = `Created: ${createdStr}`;

  const executeAction = (fn: () => void) => { 
    fn(); 
    document.dispatchEvent(new CustomEvent("closeOtherDropdowns", { detail: { dropdownId: "close-all" } })); 
  };

  const menuOptions = [
    { label: "Rename", onClick: () => executeAction(() => openModal("renameReport", { id: report.id, name: report.name })) },
    ...(!report.isDraft && !report.isBookmarked ? [{ 
      label: "Save to My Reports", 
      onClick: () => executeAction(() => actionMutation.mutate({ id: report.id, action: TOGGLE_BOOKMARK, isBookmarked: true })) 
    }] : []),
    {
      label: report.isDraft ? "Revert / Discard" : "Delete",
      onClick: () =>
        executeAction(() =>
          openModal("deleteReport", {
            id: report.id,
            action: report.isDraft ? REVERT_DRAFT : DELETE_REPORT,
            parentReportId: report.parentReportId,
          }),
        ),
    },
  ];

  return (
    <li onClick={() => navigate(`/reports/${report.id}`)} className={`side-panel-item ${String(reportId) === String(report.id) ? "highlight" : ""} ${isSelectable ? "selectable" : ""}`}>
      {isSelectable && !isLocked && (
        <div className="item-checkbox" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={isSelected} onChange={() => toggleSelection(report.id)} />
        </div>
      )}

      <div className="item-content">
        <div className="item-header margin-bottom-0">
          <div className="item-title-wrapper">
            <h3 className="item-title" title={displayName}>{displayName}</h3>
            {report.isDraft && <span className="draft-badge margin-left-1">Draft</span>}
            {isLocked && <i className="ri-loader-4-line spin text-muted margin-left-1" title="Dashboard refreshing..." />}
          </div>
          
          <div className="item-actions-wrapper">
            <div className="item-info-trigger margin-start-1" title={infoTooltip} onClick={(e) => e.stopPropagation()}>
               <i className="ri-information-line" />
            </div>

            <div className="item-actions-trigger" onClick={(e) => e.stopPropagation()}>
               <DropDownWithPortal id={`report-menu-${report.id}`} options={menuOptions} disabled={isLocked} />
            </div>
          </div>
        </div>

        <div className="item-meta">
          <span>Last run {lastRunStr}</span>
        </div>
        
      </div>
    </li>
  );
};

export default SidePanelItem;
